import { ComponentPropsWithoutRef } from "react";
import styles from "./FileLayoutSlider.module.css";
import SingleLayoutIcon from "./images/single-layout.svg?react";
import GridLayoutIcon from "./images/grid-layout.svg?react";
import CoreSlider, { CoreSliderProps } from "../slider";
import i18nPackage from "../../i18n/i18n";
import { CoreIconButton, CoreIconButtonProps } from "../button/Button";

type FileLayoutIconProps = {
	children: React.ReactNode;
	onClick: () => void;
} & Omit<CoreIconButtonProps, "children" | "onClick">;

function FileLayoutIcon({ children, onClick, ...props }: FileLayoutIconProps) {
	return (
		<CoreIconButton
			{...props}
			onClick={onClick}
			variant="plain"
			className={`${styles.fileLayoutIcon} ${props.className || ""}`}
		>
			{children}
		</CoreIconButton>
	);
}

type CoreLayoutSliderProps = {
	/**@default 1 */
	min?: number;
	/**@default 5 */
	max?: number;
	onChange: (value: number) => void;
	value: number;
	slotProps?: {
		container?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		leftIcon?: FileLayoutIconProps;
		rightIcon?: FileLayoutIconProps;
		slider?: Omit<CoreSliderProps, "onChange" | "value" | "min" | "max">;
	};
};

export default function CoreLayoutSlider({ onChange, value, min = 1, max = 5, slotProps }: CoreLayoutSliderProps) {
	const handleClickLeftIcon = () => {
		if (value > min) {
			onChange(value - 1);
		}
	};

	const handleClickRightIcon = () => {
		if (value < max) {
			onChange(value + 1);
		}
	};

	return (
		<div
			{...slotProps?.container}
			className={`${styles.fileLayoutSliderContainer} ${slotProps?.container?.className || ""}`}
		>
			<FileLayoutIcon {...slotProps?.leftIcon} onClick={handleClickLeftIcon}>
				{slotProps?.leftIcon?.children ?? <SingleLayoutIcon />}
			</FileLayoutIcon>
			<CoreSlider
				value={value}
				onChange={onChange}
				min={min}
				max={max}
				ariaLabel={i18nPackage.t("coreComponents.fileLayoutSlider.ariaLabel")}
				{...slotProps?.slider}
				className={`${styles.slider} ${slotProps?.slider?.className || ""}`}
			/>
			<FileLayoutIcon {...slotProps?.rightIcon} onClick={handleClickRightIcon}>
				{slotProps?.rightIcon?.children ?? <GridLayoutIcon />}
			</FileLayoutIcon>
		</div>
	);
}
